<script setup lang="ts">
    import { Checkbox, InputRow, Select } from '@/components/ui/form';
    import { computed } from 'vue';
    import { Input } from '@/components/ui/input';
    import { OnboardingStep } from '@/types';
    import { Textarea } from '@/components/ui/textarea';
    import { useI18n } from 'vue-i18n';
    import { useOnboardingStepData } from '@/composables/useOnboardingStepData';
    import type { SelectOption } from '@/types';
    import type { Table } from '@tanstack/vue-table';

    const props = defineProps<{
        form: any;
        table: Table<OnboardingStep>;
        disableServiceChange?: boolean;
    }>();

    const { t } = useI18n();

    const { onboardingStepOptions } = useOnboardingStepData();

    const statusOptions = computed<SelectOption[]>(() => {
        if (!onboardingStepOptions.value) return [];

        const existingServices = new Set(
            props.table.getRowModel().rows.map((row) => row.original.service),
        );

        return onboardingStepOptions.value
            .filter((onboardingStep: OnboardingStep) => {
                return (
                    onboardingStep.service === 'other' ||
                    !existingServices.has(onboardingStep.service) ||
                    onboardingStep.service === props.form.service
                );
            })
            .map((onboardingStep: OnboardingStep) => ({
                value: onboardingStep.service,
                label: t(`onboarding.services.${onboardingStep.service}`),
            }));
    });

    const isDefault = computed(() => {
        return !!props.form.service && props.form.service !== 'other';
    });

    const isOther = computed(() => {
        return props.form.service === 'other';
    });

    const descriptionPlaceholder = computed(() => {
        if (isDefault.value) {
            return t(`onboarding.steps.${props.form.service}.description`);
        }

        return t('onboarding.form.description_placeholder');
    });
</script>

<template>
    <div class="grid grid-cols-1 gap-3">
        <InputRow
            :form="form"
            name="service"
            label="onboarding.form.service"
        >
            <Select
                v-model="form.service"
                :options="statusOptions"
                :disabled="disableServiceChange"
            />
        </InputRow>
        <InputRow
            v-if="isOther"
            :form="form"
            name="other_title"
            label="onboarding.form.title"
        >
            <Input
                v-model="form.other_title"
                :placeholder="t('onboarding.steps.other.title')"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="description"
            label="onboarding.form.description"
        >
            <Textarea
                id="description"
                v-model="form.description"
                :placeholder="descriptionPlaceholder"
                :disabled="isDefault"
            />
        </InputRow>
        <Checkbox
            v-if="isOther"
            id="allow_comments"
            v-model="form.allow_comments"
            label="onboarding.form.allow_comments"
        />
        <Checkbox
            v-if="isOther"
            id="allow_files"
            v-model="form.allow_files"
            label="onboarding.form.allow_files"
        />
    </div>
</template>
